import { LitElement, html, css } from 'lit';
import SnapshotController from '../snapshot/SnapshotController.js';
import './SnapshotSaveDialog.js';
import templateSelectedRootStore from './TemplateSelectedRootStore.js';
import { resetRoot } from './TemplateSelectedRootSlice.js';

class SnapshotManager extends LitElement {
    static styles = css`
        :host {
            display: none;
        }
        :host([visible]) {
            display: block;
        }

        .background {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0,0,0,0.7);
            z-index: 2000;
        }
        .container {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 80vw;
            height: 80vh;
            background: white;
            border-radius: 10px;
            z-index: 2001;
            display: flex;
            flex-direction: column;
            border: 1px solid #cdcdcd;
            overflow: hidden;
        }
        .header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 20px;
            background-color: #f0f0f0;
            user-select: none;
            font-weight: bold;
            border-bottom: 1px solid rgb(204, 204, 204);
            border-top-left-radius: 10px;
            border-top-right-radius: 10px;
        }
        .header h3 {
            margin: 0;
            flex-grow: 1;
            text-align: center;
            padding-left: 20px;
        }
        .close-btn {
            cursor: pointer;
            font-size: 18px;
        }
        .body {
            flex-grow: 1;
            overflow-y: auto;
            padding: 15px;
        }
        .new-snapshot-area {
            padding-bottom: 15px;
            margin-bottom: 15px;
            text-align: center;
            border-bottom: 1px solid #eee;
        }
        .snapshot-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        .snapshot-item {
            border: 1px solid #eee;
            border-radius: 5px;
            padding: 10px;
            margin-bottom: 10px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
        .snapshot-info h4 { margin: 0 0 5px 0; }
        .snapshot-info p { margin: 0; font-size: 0.9em; color: #555; }
        .snapshot-info time { font-size: 0.8em; color: #888; }
        .snapshot-actions button { margin-left: 10px; }
        .no-snapshots { text-align: center; color: #888; margin-top: 20px; }

        .snapshot-button {
            background-color: rgb(0, 123, 255);
            border-radius: 30px;
            border: none;
            color: white;
            text-align: center;
            text-decoration: none;
            display: inline-block;
            font-size: 16px;
            transition-duration: 0.4s;
            padding: 6px 15px;
            cursor: pointer;
        }
        .snapshot-button.red {
            background-color: rgba(255, 94, 94, 1);
        }
        .snapshot-button.dark {
            background-color: rgba(88, 134, 176, 1);
        }
        .snapshot-button.gray {
            background-color: rgba(163, 163, 163, 1);
        }

        .snapshot-notice {
            font-size: 0.85em;
            color: #856404;
            background-color: #fff3cd;
            border: 1px solid #ffeeba;
            border-radius: 5px;
            padding: 10px;
            margin-bottom: 15px;
            text-align: left;
            line-height: 1.4;
        }
        .import-label {
            display: inline-block;
            cursor: pointer;
        }
        .import-input {
            display: none;
        }
    `;

    static properties = {
        visible: { type: Boolean, reflect: true },
        snapshots: { type: Array }
    };

    constructor() {
        super();
        this.visible = false;
        this.snapshots = [];
    }

    async show() {
        this.visible = true;
        await this._loadSnapshots();
    }

    hide() {
        this.visible = false;
    }

    async _loadSnapshots() {
        this.snapshots = await SnapshotController.getSnapshotsForCurrentProject();
    }

    _handleNewSnapshot() {
        this.shadowRoot.querySelector('snapshot-save-dialog').show();
    }

    async _handleSaveSnapshot(e) {
        const { name, description } = e.detail;
        await SnapshotController.createSnapshot(name, description);
        await this._loadSnapshots();
    }

    async _handleRestore(snapshotId) {
        if (confirm("現在の編集内容は破棄され、選択したスナップショットの状態に復元されます。よろしいですか？")) {
            try {
                await SnapshotController.restoreSnapshot(snapshotId);
                alert("スナップショットを復元しました。");
                templateSelectedRootStore.dispatch(resetRoot());
                this.hide();
            } catch (error) {
                alert(`復元に失敗しました: ${error.message}`);
            }
        }
    }

    async _handleDelete(snapshotId) {
        if (confirm("このスナップショットを完全に削除します。よろしいですか？")) {
            await SnapshotController.deleteSnapshot(snapshotId);
            await this._loadSnapshots();
        }
    }

    // インポート処理のハンドラを追加
    async _handleImport(e) {
        const inputElement = e.target;
        const file = inputElement?.files?.[0];
        if (!file) return;

        const confirmed = confirm(
            `現在の編集内容がファイル「${file.name}」の内容で上書きされます。\n` +
            `インポート直前の状態は自動的にスナップショットとして保存されます。\n\n続行しますか？`
        );

        if (confirmed) {
            try {
                // 結果を変数に受ける
                const result = await SnapshotController.importProjectFromFile(file);

                // result が null の場合は、コントローラー内のバージョン警告で「キャンセル」されたことを意味する
                if (result === null) {
                    // 何もせず終了（成功メッセージも出さない）
                    if (inputElement) inputElement.value = '';
                    return;
                }

                alert("プロジェクトを正常にインポートしました。");
                await this._loadSnapshots();
            } catch (error) {
                alert(`インポートに失敗しました: ${error.message}`);
            }
        }

        if (inputElement) {
            inputElement.value = '';
        }
    }

    render() {
        return html`
            <snapshot-save-dialog @save-snapshot=${this._handleSaveSnapshot}></snapshot-save-dialog>
            <div class="background" @click=${this.hide}>
                <div class="container" @click=${e => e.stopPropagation()}>
                    <div class="header">
                        <h3>スナップショット管理</h3>
                        <span class="close-btn" @click=${this.hide}>×</span>
                    </div>
                    <div class="body">
                        <div class="new-snapshot-area">
                            <button class="snapshot-button" @click=${this._handleNewSnapshot}>新規スナップショット</button>
                            <!-- エクスポートボタン -->
                            <button class="snapshot-button dark" @click=${() => SnapshotController.exportProjectToFile()}>
                                <i class="fa-solid fa-file-export"></i> ファイルに出力
                            </button>

                            <!-- インポートボタン (隠しinputを使用) -->
                            <label class="snapshot-button gray import-label">
                                <i class="fa-solid fa-file-import"></i> インポート
                                <input type="file" class="import-input" accept=".pwappy,.json" @change=${this._handleImport}>
                            </label>
                        </div>
                        <p class="snapshot-notice">
                            <strong>【⚠ 注意】</strong><br>
                            スナップショットは、この端末内での一時的な作業履歴のバックアップとして機能します。<br>
                            「保存して閉じる」または「保存せずに閉じる」操作を実行した場合、<strong>すべてのスナップショットは自動的に削除されます。</strong><br>
                            重要な変更は、定期的に明示的にアプリケーションを保存してください。
                        </p>
                        ${this.snapshots.length > 0 ? html`
                            <ul class="snapshot-list">
                                ${this.snapshots.map(s => html`
                                    <li class="snapshot-item">
                                        <div class="snapshot-info">
                                            <h4>${s.name}</h4>
                                            <p>${s.description}</p>
                                            <time>${new Date(s.timestamp).toLocaleString()}</time>
                                        </div>
                                        <div class="snapshot-actions">
                                            <button class="snapshot-button" @click=${() => this._handleRestore(s.id)}>復元</button>
                                            <button class="snapshot-button red" @click=${() => this._handleDelete(s.id)}>削除</button>
                                        </div>
                                    </li>
                                `)}
                            </ul>
                        ` : html`
                            <p class="no-snapshots">保存されているスナップショットはありません。</p>
                        `}
                    </div>
                </div>
            </div>
        `;
    }
}

customElements.define('snapshot-manager', SnapshotManager);